import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  Divider,
  Tooltip,
  styled,
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getTeachersDirect, getTimetableRecordsDirect, getUnavailabilityDirect } from '@timetable/api';
import { queryKeys } from '../../lib/queryKeys';
import { useAuthStore } from '../../store/authStore';
import { LoadingSpinner } from '../LoadingSpinner';
import { EmptyState } from '../EmptyState';
import type { TeacherWithRelations, DayOfWeek, SchedulePeriod } from '@timetable/types';
import { useMemo } from 'react';

const SidebarContainer = styled(Paper)(() => ({
  width: 320,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

const TeacherListContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
});

const TeacherListItem = styled(ListItem)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const WorkloadBar = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

interface AvailableTeachersSidebarProps {
  selectedDay: DayOfWeek | null;
  selectedPeriod: SchedulePeriod | null;
}

interface TeacherWithWorkload extends TeacherWithRelations {
  workload: number;
  isAvailable: boolean;
  unavailabilityReason?: string;
}

export function AvailableTeachersSidebar({
  selectedDay,
  selectedPeriod,
}: AvailableTeachersSidebarProps) {
  const { schoolContext } = useAuthStore();

  // Fetch all teachers
  const { data: teachers, isLoading: loadingTeachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || '' }),
    enabled: !!schoolContext,
  });

  // Fetch all timetable records for workload calculation
  const { data: allRecords, isLoading: loadingRecords } = useQuery({
    queryKey: queryKeys.timetable.list({ school_id: schoolContext || '' }),
    queryFn: () => getTimetableRecordsDirect({ school_id: schoolContext || '' }),
    enabled: !!schoolContext,
  });

  // Fetch unavailability records
  const { data: unavailability, isLoading: loadingUnavailability } = useQuery({
    queryKey: queryKeys.unavailability.list({ school_id: schoolContext || '' }),
    queryFn: () => getUnavailabilityDirect(schoolContext || ''),
    enabled: !!schoolContext,
  });

  // Calculate available teachers with workload
  const availableTeachers = useMemo<TeacherWithWorkload[]>(() => {
    if (!teachers || !allRecords || !unavailability || !selectedDay || !selectedPeriod) {
      return [];
    }

    // Get today's date in YYYY-MM-DD format for unavailability checking
    const today = new Date().toISOString().split('T')[0];

    return teachers
      .map((teacher) => {
        // Calculate total workload (number of periods assigned)
        const workload = allRecords.filter((r) => r.teacher_id === teacher.id).length;

        // Check if teacher is assigned in the selected period using time-overlap
        const isAssignedInPeriod = allRecords.some(
          (r) =>
            r.teacher_id === teacher.id &&
            r.day_of_week === selectedDay &&
            r.period?.time_start != null &&
            r.period?.time_end != null &&
            r.period.time_start < selectedPeriod.time_end &&
            r.period.time_end > selectedPeriod.time_start
        );

        // Check if teacher is unavailable on today's date
        const unavailabilityRecord = unavailability.find(
          (u) => u.teacher_id === teacher.id && u.date === today
        );

        const isAvailable = !isAssignedInPeriod && !unavailabilityRecord;

        return {
          ...teacher,
          workload,
          isAvailable,
          unavailabilityReason: unavailabilityRecord?.reason || undefined,
        };
      })
      .filter((t) => t.isAvailable) // Only show available teachers
      .sort((a, b) => a.workload - b.workload); // Sort by workload (ascending)
  }, [teachers, allRecords, unavailability, selectedDay, selectedPeriod]);

  // Calculate max workload for progress bar scaling
  const maxWorkload = useMemo(() => {
    if (availableTeachers.length === 0) return 1;
    return Math.max(...availableTeachers.map((t) => t.workload), 1);
  }, [availableTeachers]);

  const isLoading = loadingTeachers || loadingRecords || loadingUnavailability;

  if (isLoading) {
    return (
      <SidebarContainer elevation={3}>
        <SidebarHeader>
          <Typography variant="h6">Available Teachers</Typography>
        </SidebarHeader>
        <Box sx={{ p: 2 }}>
          <LoadingSpinner />
        </Box>
      </SidebarContainer>
    );
  }

  return (
    <SidebarContainer elevation={3}>
      <SidebarHeader>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          Available Teachers
        </Typography>
        {selectedDay && selectedPeriod && (
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            {selectedDay} - Period {selectedPeriod.period_number}
          </Typography>
        )}
      </SidebarHeader>

      <TeacherListContainer>
        {!selectedDay || !selectedPeriod ? (
          <Box sx={{ p: 2 }}>
            <EmptyState
              title="No Period Selected"
              description="Click on a period in the timetable to see available teachers"
            />
          </Box>
        ) : availableTeachers.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <EmptyState
              title="No Available Teachers"
              description="All teachers are either assigned or unavailable for this period"
            />
          </Box>
        ) : (
          <List disablePadding>
            {availableTeachers.map((teacher) => (
              <TeacherListItem key={teacher.id}>
                {/* Teacher Name and Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <ListItemText
                    primary={teacher.name}
                    secondary={teacher.teacher_type.type_name}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </Box>

                {/* Assigned Subjects */}
                {teacher.subjects && teacher.subjects.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                    {teacher.subjects.map((subject) => (
                      <Chip
                        key={subject.id}
                        label={subject.subject_name}
                        size="small"
                        sx={{
                          backgroundColor: subject.color_code || '#e0e0e0',
                          color: '#000',
                          fontSize: '0.7rem',
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Workload Indicator */}
                <WorkloadBar>
                  <Tooltip
                    title={`${teacher.workload} period${teacher.workload !== 1 ? 's' : ''} assigned`}
                    arrow
                  >
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(teacher.workload / maxWorkload) * 100}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor:
                              teacher.workload < maxWorkload * 0.5
                                ? 'success.main'
                                : teacher.workload < maxWorkload * 0.8
                                ? 'warning.main'
                                : 'error.main',
                          },
                        }}
                      />
                    </Box>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
                    {teacher.workload} periods
                  </Typography>
                </WorkloadBar>
              </TeacherListItem>
            ))}
          </List>
        )}
      </TeacherListContainer>

      {/* Footer with summary */}
      {selectedDay && selectedPeriod && availableTeachers.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
            <Typography variant="caption" color="text.secondary">
              {availableTeachers.length} teacher{availableTeachers.length !== 1 ? 's' : ''}{' '}
              available
            </Typography>
          </Box>
        </>
      )}
    </SidebarContainer>
  );
}
